/**
 * Module valuation-facts defines schemas and types for location and market facts,
 * including sources, points of interest, ratings, time series, and emissions.
 *
 * Generic coordinates, regions, and travel measurements are defined in geo.ts.
 */
import { z } from 'zod';
import { ISODateSchema, UUIDSchema } from './common';
import { CoordinatesSchema, ReachabilityItemSchema, RegionSchema, ValuationRegionTypeSchema } from './geo';
import { PercentageSchema } from './measurements';
import { MonetaryValueSchema, SquareMetreMoneyPerAreaValueSchema } from './money';
const OriginSchema = z.object({
    title: z.string(),
    link: z.string(),
    date: ISODateSchema,
});
const FactReachabilitySchema = z.object({
    linear_distance_selected: z.boolean(),
    linear_distance_meters: z.number(),
    driving: ReachabilityItemSchema.optional(),
    public_transport: ReachabilityItemSchema.optional(),
    walking: ReachabilityItemSchema.optional(),
    bicycling: ReachabilityItemSchema.optional(),
});
const PointOfInterestTypeSchema = z.enum([
    'bus',
    'tram',
    'subway',
    'train',
    'motorway',
    'supermarket',
    'pharmacy',
    'doctor',
    'restaurant',
    'gym',
    'museum',
    'school',
    'park',
    'airport',
]);
const PointOfInterestFactSchema = z.object({
    uuid: UUIDSchema,
    title: z.string(),
    coordinates: CoordinatesSchema,
    origin: OriginSchema,
    type: PointOfInterestTypeSchema,
    reachability: FactReachabilitySchema,
});
const NumberPointsOfInterestFactItemSchema = z.object({
    count: z.number(),
    radius_meter: z.number(),
});
const NumberPointsOfInterestFactSchema = z.object({
    bus: NumberPointsOfInterestFactItemSchema.optional(),
    tram: NumberPointsOfInterestFactItemSchema.optional(),
    subway: NumberPointsOfInterestFactItemSchema.optional(),
    train: NumberPointsOfInterestFactItemSchema.optional(),
    airport: NumberPointsOfInterestFactItemSchema.optional(),
    motorway: NumberPointsOfInterestFactItemSchema.optional(),
    restaurant: NumberPointsOfInterestFactItemSchema.optional(),
    supermarket: NumberPointsOfInterestFactItemSchema.optional(),
    pharmacy: NumberPointsOfInterestFactItemSchema.optional(),
    doctor: NumberPointsOfInterestFactItemSchema.optional(),
    gym: NumberPointsOfInterestFactItemSchema.optional(),
    museum: NumberPointsOfInterestFactItemSchema.optional(),
    school: NumberPointsOfInterestFactItemSchema.optional(),
    park: NumberPointsOfInterestFactItemSchema.optional(),
});
const RatingFactSchema = z.object({
    connection: z.number().optional(),
    local_supply: z.number().optional(),
    leisure: z.number().optional(),
    surroundings: z.number().optional(),
    economics: z.number().optional(),
    demographics: z.number().optional(),
    education: z.number().optional(),
    real_estate_economics: z.number().optional(),
    real_estate_structure: z.number().optional(),
    environment: z.number().optional(),
    social: z.number().optional(),
    governance: z.number().optional(),
    structures: z.number().optional(),
});
const PeriodSchema = z.enum(['day', 'week', 'month', 'quarter', 'year']);
const StatsTypeSchema = z.enum(['minimum', 'average', 'maximum']);
const TopicTypeSchema = z.enum([
    'gross_domestic_product',
    'gross_domestic_product_ranking',
    'employment',
    'unemployment',
    'unemployment_ranking',
    'population',
    'building_permit_apartments',
    'area',
    'population_density',
    'population_ranking',
    'price_apartment_existing',
    'price_apartment_new',
    'price_townhouse_existing',
    'price_townhouse_new',
    'price_plot_single_family_house',
    'rent_apartment_existing',
    'rent_apartment_new',
    'price_single_family_house',
    'retail_rent_city',
    'retail_rent_1b',
    'retail_rent_secondary',
    'retail_yield_decentral',
    'retail_yield_central',
    'office_rent_city',
    'office_rent_fringe',
    'office_rent_suburb',
    'office_rent_agglomeration',
    'office_yield_decentral',
    'office_yield_central',
    'logistics_rent_logistics',
    'logistics_rent_service',
    'logistics_yield_logistics',
    'logistics_yield_industrial',
    'logistics_yield_logistics_prime',
    'logistics_yield_industrial_prime',
]);
const createTimeSeriesValueSchema = (valueSchema) => z.object({ value: valueSchema, date: ISODateSchema });
const createTimeSeriesStatisticsSchema = (valueSchema, developmentSchema) => z.object({
    rank: z.number().optional(),
    latest_value: valueSchema.optional(),
    development_1: developmentSchema.optional(),
    development_5: developmentSchema.optional(),
});
const NumberTimeSeriesValueSchema = createTimeSeriesValueSchema(z.number());
const MonetaryTimeSeriesValueSchema = createTimeSeriesValueSchema(MonetaryValueSchema);
const MoneyPerAreaTimeSeriesValueSchema = createTimeSeriesValueSchema(SquareMetreMoneyPerAreaValueSchema);
const NumberTimeSeriesStatisticsSchema = createTimeSeriesStatisticsSchema(z.number(), PercentageSchema);
const PercentageTimeSeriesStatisticsSchema = createTimeSeriesStatisticsSchema(PercentageSchema, z.number());
const MonetaryTimeSeriesStatisticsSchema = createTimeSeriesStatisticsSchema(MonetaryValueSchema, PercentageSchema);
const MoneyPerAreaTimeSeriesStatisticsSchema = createTimeSeriesStatisticsSchema(SquareMetreMoneyPerAreaValueSchema, PercentageSchema);
const ValuationRegionSchema = RegionSchema.extend({
    type: ValuationRegionTypeSchema,
});
const BaseTimeSeriesFactSchemaShape = {
    title: z.string(),
    origin: OriginSchema,
    region: ValuationRegionSchema,
    period: PeriodSchema,
    topic: TopicTypeSchema,
    stats_type: StatsTypeSchema.optional(),
};
const NumberTimeSeriesFactSchema = z.object({
    ...BaseTimeSeriesFactSchemaShape,
    value_type: z.enum(['float', 'integer']),
    values: NumberTimeSeriesValueSchema.array(),
    statistics: NumberTimeSeriesStatisticsSchema,
});
const PercentageTimeSeriesFactSchema = z.object({
    ...BaseTimeSeriesFactSchemaShape,
    value_type: z.literal('percentage'),
    values: NumberTimeSeriesValueSchema.array(),
    statistics: PercentageTimeSeriesStatisticsSchema,
});
const MonetaryTimeSeriesFactSchema = z.object({
    ...BaseTimeSeriesFactSchemaShape,
    value_type: z.literal('monetary_value'),
    values: MonetaryTimeSeriesValueSchema.array(),
    statistics: MonetaryTimeSeriesStatisticsSchema,
});
const MoneyPerAreaTimeSeriesFactSchema = z.object({
    ...BaseTimeSeriesFactSchemaShape,
    value_type: z.literal('money_per_area_value'),
    values: MoneyPerAreaTimeSeriesValueSchema.array(),
    statistics: MoneyPerAreaTimeSeriesStatisticsSchema,
});
const TimeSeriesFactSchema = z.discriminatedUnion('value_type', [
    NumberTimeSeriesFactSchema,
    PercentageTimeSeriesFactSchema,
    MonetaryTimeSeriesFactSchema,
    MoneyPerAreaTimeSeriesFactSchema,
]);
const NoisePollutionRatingsSchema = z.object({
    road_noise: z.string().optional(),
    rail_noise: z.string().optional(),
    aircraft_noise: z.string().optional(),
    industrial_noise: z.string().optional(),
    description: z.string().optional(),
});
const EmissionsImmissionsSchema = z.object({
    noise_ratings: NoisePollutionRatingsSchema.optional(),
    flooding_risk: z.string().optional(),
    earthquake_zone: z.string().optional(),
});
export const FactsSchema = z.object({
    rating: RatingFactSchema,
    points_of_interest: PointOfInterestFactSchema.array(),
    number_points_of_interest: NumberPointsOfInterestFactSchema,
    time_series: TimeSeriesFactSchema.array(),
    emissions_immissions: EmissionsImmissionsSchema.optional(),
});
